import React, { useEffect, useState } from 'react'
import { useCustomerIBVReportsMutation, usePersonBaseDataQuery, useStaticDataQuery } from '../redux/services/services'
import { decryptFernet } from '../helpers/crypt'
import jwt_decode from 'jwt-decode'
import { notUndefinedNull } from '../helpers/shared'
import { trimLowerCase, isUndefinedNull } from '../helpers/shared'
import { useDispatch, useSelector } from 'react-redux'
import { getStaticData } from '../redux/actions/staticData'
import { setMessage } from '../redux/actions/message'
import { useNavigate } from 'react-router-dom'
import { statusRequest } from '../constants/base'
import { BASE_AUTOLOGIN_URL, BASE_IBV } from '../redux/types/base'
import { USER_LOGIN, USER_APP_ORIGIN, USER_INFO_PRODUCT_REQUEST } from '../redux/types/user'
import { getUserImages, sendCheckUserTier, getRejectedFiles } from '../redux/actions/files'
import { getBase } from '../redux/actions/base'
import { setLoader } from '../redux/actions/shared'
import { store } from '../redux/store/store'
import { slimTier } from '../constants/form'
import { routes, siteMap } from '../constants/routes'
import { appsOrigin } from '../constants/appOrigin'
import { getPersonData } from '../redux/actions/personData'
import { getUserApplications } from '../redux/actions/user'
import { logoBgAppOrigin } from '../constants/appOrigin'
import { useTranslation } from 'react-i18next'

const useLoginByGivenToken = ({ token }) => {
  const [localUserInfo, setLocalUserInfo] = useState(null)
  const [localAppOriginData, setLocalAppOriginData] = useState(appsOrigin.lendecy)
  const [appCode, setAppCode] = useState(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { applications } = useSelector(state => state.user)
  const personData = useSelector(state => state.personData.data)
  const { t } = useTranslation()

  /* 
  * Listen for value in 'token' and call to mutation (customIBVReports) 
  */
  const [customerIBVReports, { isLoading, data: customerIBVReportsData, error: customerIBVReportsError }] = useCustomerIBVReportsMutation()
  useEffect(() => {
    if (notUndefinedNull(token)) {
      try {
        const decryptedToken = decryptFernet(token);
        const userInfo = jwt_decode(decryptedToken);
        if (userInfo) {
          const {
            info,
            last_product_request_id,
            go_to_step,
            verification_url
          } = userInfo
          let [url, chirpRequestCode] = verification_url?.split('requestCode=')
          let data = {
            request_code: chirpRequestCode,
            ibv_report: userInfo?.ibv_report,
            bifrost_user_id: info?.user_id,
          }
          setLocalUserInfo(userInfo)
          customerIBVReports(data) // * Calling to mutation
        }
      } catch (error) {
        console.error(error)
      }
    }
  }, [token])

  /* 
  When data is received from the 'customerIBVReports' mutation
  There is actually not much to do in this useEffect, as the login process continues in the next useEffect
  */
  useEffect(() => {
    const handleCustomerIBVReports = async () => {
      if (customerIBVReportsError) {
        console.error(customerIBVReportsError)
        dispatch(setMessage(t("message.errorAutologinIbv"), 'error'));
        setTimeout(() => {
          navigate(siteMap.logout.path)
        }, 667);
        
      }
    }
    handleCustomerIBVReports()
  }, [customerIBVReportsData, customerIBVReportsError])

  // * Call to personBaseDataQuery
  const { 
    data: personDataData, error: personDataError, isLoading: personDataLoading 
  } = usePersonBaseDataQuery( 
    { 
      userId: localUserInfo?.info?.user_id, 
      productRequestId: localUserInfo?.last_product_request_id || null 
    }, 
    { 
      // * Trigger query only when localUserInfo is available
      skip: isUndefinedNull(localUserInfo),
      refetchOnMountOrArgChange: true,
    }
  )
  
  //* When personData is received from the query, continue the login process below    
  useEffect(() => {
    const handlePersonData = async () => {
      const {
        appOriginData,
        status: personStatus,
        code
      } = await dispatch(getPersonData(null, null, personDataData))
      setLocalAppOriginData(appOriginData)
      setAppCode(code)
      let productRequestId = localUserInfo?.last_product_request_id || null
      let isVerified, status
      if (customerIBVReportsData?.success) { 
        status = customerIBVReportsData?.success?.data?.status
        isVerified = (trimLowerCase(status) === 'created') ? false : true
      }
      if (isUndefinedNull(productRequestId)) {
        if(notUndefinedNull(personData?.product_request)) {
          productRequestId = personData?.product_request?.id
        } else {
          dispatch(setMessage(t("message.errorGetUserInfo"), 'error'));
          navigate(routes.login(appsOrigin.default))
        }
      }

    }

    if (personDataData) handlePersonData()

  }, [personDataData])

  useEffect(() => {
    if (personDataError) {
      console.error(personDataError)
      dispatch(setMessage(t("common.errorPerson"), 'error'));
      setTimeout(() => {
        navigate(siteMap.logout.path)
      }, 667);
    }

  }, [personDataError])


  let isVerified = trimLowerCase(customerIBVReportsData?.success?.data?.status) === 'created' ? false : true;
  let personStatus = trimLowerCase(personDataData?.success?.product_request?.status?.unique_description)
  const { 
    data: staticDataData,
    error: staticDataError,
  } = useStaticDataQuery({
    skip: !personDataData || isVerified && (personStatus !== statusRequest.sent) 
  })
  //* When staticData is received from the query, we finish the login process and redirect
  useEffect(() => {
    const handleStaticData = async () => {
      if (staticDataData && customerIBVReportsData) {
        if (!isVerified && personStatus !== statusRequest.sent) {
          // Happy path
          if (await dispatch(getStaticData(staticDataData))) {
            try {
                dispatch({
                  type: BASE_AUTOLOGIN_URL,
                  payload: {
                    active: true,
                    goToStep: 0,
                    token,
                    isUsed: true
                  }
                })
                dispatch({
                  type: BASE_IBV,
                  payload: {
                    requestWidgetURL: localUserInfo.verification_url,
                    isIBVVerified: isVerified,
                    isIBVRequired: true,
                    isIBVShown: true
                  }
                })
                dispatch({
                  type: USER_LOGIN,
                  payload: {
                    userIdBifrost: localUserInfo.info.user_id,
                    token: token,
                    lastProductRequestId: localUserInfo.last_product_request_id
                  }
                });
                dispatch({ 
                  type: USER_APP_ORIGIN, 
                  payload: localAppOriginData 
                });
                await dispatch(getUserImages(localUserInfo.info.user_id));
                await dispatch(getBase());
                if (await dispatch(sendCheckUserTier(appCode))) {
                  dispatch(getRejectedFiles());
                  // dispatch({ type: USER_APP_ORIGIN, payload: localAppOriginData });
                  dispatch(setLoader(false));
                } else {
                  dispatch(setLogout());
                  dispatch(setLoader(false));
                  dispatch(setMessage(t("message.onLogin"), 'error'));
                }
                let productsAndServices = [];
                if (!applications) {
                  productsAndServices = await dispatch(getUserApplications());
                } else {
                  productsAndServices = applications;
                }
                let product = productsAndServices.filter(
                  product => product?.application?.unique_name === localAppOriginData);
                if (product.length === 0) product = [{image_main: logoBgAppOrigin.default }];
                const [productRequest] = product;
                dispatch({ type: USER_INFO_PRODUCT_REQUEST, payload: productRequest });
                // Deciding where to redirect, after being logged in 
                let currentParam
                const last_regular_tier = store.getState()?.personData?.data?.last_regular_tier
                currentParam = last_regular_tier?.unique_name === slimTier ? 'edit' : 'add'
                const slimRoute = routes.tier(slimTier, currentParam)
                navigate(slimRoute)
    
            } catch (error) {
              console.error('Error in handleToken.LoginBifrost', error);
              dispatch(setMessage(t("message.errorAutologinIbv"), 'error'));
              return navigate(siteMap.logout.path)

            }
          }
        } else {
          dispatch(setMessage(t("ibv.ibvCodeAlreadyValidatedOrExpired"), 'info'));
          return navigate(siteMap.logout.path)

        } 
      }
    }
    if (staticDataData && appCode) handleStaticData()
  }, [staticDataData, customerIBVReportsData, appCode])

  /* useEffect(() => {
    if (staticDataError) {
      console.error(staticDataError)
      dispatch(setMessage(t("common.errorStatic"), 'error'));
      setTimeout(() => {
        navigate(siteMap.logout.path)
      }, 667);
    }
  }, [staticDataError]) */

  return {
  
  }
}

export default useLoginByGivenToken
